import { Avatar, CircularProgress } from '@mui/material';
import type { AvatarProps } from '@mui/material';
import { useGetContributorProfilePhotoUrl } from '@/state/queries/contributors/use-get-contributor-profile-photo-url';
import { useState } from 'react';

interface ProfilePhotoAvatarProps extends Omit<AvatarProps, 'src'> {
  contributorId?: string | undefined;
  assetName?: string | undefined;
  displayName?: string;
  fallbackChar?: string;
  src?: string; // Allow direct src override (e.g., for UserContributor photos)
}

/**
 * Reusable Avatar component for displaying contributor profile photos.
 * Automatically fetches the download URL on-demand using the assetName.
 */
export const ProfilePhotoAvatar = ({
  contributorId,
  assetName,
  displayName,
  fallbackChar,
  src: directSrc,
  ...avatarProps
}: ProfilePhotoAvatarProps) => {
  // If direct src is provided (e.g., UserContributor photoUri), use it directly
  const shouldUseDirectSrc = !!directSrc;
  const {
    data: photoUrl,
    isLoading,
    error,
  } = useGetContributorProfilePhotoUrl(
    shouldUseDirectSrc ? undefined : contributorId,
    shouldUseDirectSrc ? undefined : assetName,
  );
  const [imageError, setImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleImageLoad = () => {
    setImageError(false);
  };

  // Show loading spinner while fetching URL (only if not using direct src)
  if (!shouldUseDirectSrc && isLoading) {
    return (
      <Avatar
        {...avatarProps}
        sx={{ ...avatarProps.sx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress size={20} />
      </Avatar>
    );
  }

  // Determine which URL to use
  // If there's an error fetching the URL, show fallback
  const imageSrc = shouldUseDirectSrc ? directSrc : error ? null : photoUrl;
  const showFallback = !imageSrc || imageError || !!error;
  const fallbackText = fallbackChar || (displayName ? displayName.charAt(0).toUpperCase() : '?');

  return (
    <Avatar
      {...avatarProps}
      src={showFallback ? undefined : imageSrc || undefined}
      onError={handleImageError}
      onLoad={handleImageLoad}
    >
      {showFallback ? fallbackText : null}
    </Avatar>
  );
};
